import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  Select,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  InputAdornment
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Country, State } from 'country-state-city';
import {
  getCountryCallingCode,
  parsePhoneNumberFromString
} from 'libphonenumber-js';

import { PackItem } from '../RafflePage';
import { useNetwork } from '../../../../../../../utils/network';
import { trackFormError } from '../../../../../../../utils/track';

export type EntryFormValues = {
  fullName: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;

  countryIso2: string;
  countryName: string;
  stateIso2: string;
  stateName: string;

  phoneCountryCode: string;
  phone: string;
  agreeToTerms: boolean;
  eligible: 'yes' | 'no' | '';
};

type RaffleEntryFormProps = {
  packageName: string;
  paymentPath?: string;
  persistToSession?: boolean;
  onReadyToPay?: (payload: RafflePaymentPayload) => void | Promise<void>;

  selectedPack?: PackItem;
  standaloneItems: PackItem[];
  subscriptionItems: PackItem[];
  signupSource?: 'giveaway' | 'giveaway-subs' | 'giveaway-offer' | 'promo-pack';
  /** Which palette to paint. Defaults to the original light one. */
  surface?: 'light' | 'dark';
  /** Values already known when the form opens (the QR funnel's sign-up draft),
   *  laid over the defaults once, at mount. Nothing here is trusted: the server
   *  re-validates every field it is sent. */
  initialEntry?: Partial<EntryFormValues>;
};

/* The two surfaces this form can sit on.
 *
 * `light` reproduces the original palette EXACTLY and is the default, so
 * RafflePage — the only other caller — renders byte-identically to before this
 * prop existed. `dark` mirrors `features/GiveawayOffer/giveawayOfferTheme`'s
 * `darkFieldSx`, for the QR funnel's checkout dialog.
 *
 * Validation reds are deliberately NOT themed: they read correctly on both
 * grounds, and an error that changes colour with the surface is one more thing
 * to keep in step for no benefit. */
const ENTRY_SURFACES = {
  light: {
    card: '#ffffff',
    cardBorder: 'rgba(15,23,42,0.08)',
    cardShadow: '0 20px 60px rgba(15,23,42,0.08)',
    field: '#fff',
    fieldBorder: 'rgba(15,23,42,0.10)',
    fieldHover: 'rgba(15,23,42,0.22)',
    fieldFocus: '#111827',
    focusRing: '0 0 0 4px rgba(17,24,39,0.06)',
    text: '#0f172a',
    /* Several headings and labels used the CSS keyword `black` rather than the
       palette's near-black. Kept verbatim so the light surface is unchanged. */
    strong: 'black',
    accent: 'black',
    placeholder: 'rgba(15,23,42,0.35)',
    disabledText: 'rgba(15,23,42,0.75)',
    disabledPlaceholder: 'rgba(15,23,42,0.30)'
  },
  dark: {
    card: 'rgba(15,22,32,0.92)',
    cardBorder: 'rgba(140,170,190,0.16)',
    cardShadow: '0 20px 60px rgba(0,0,0,0.45)',
    field: 'rgba(255,255,255,0.04)',
    fieldBorder: 'rgba(140,170,190,0.22)',
    fieldHover: 'rgba(56,232,255,0.45)',
    fieldFocus: '#38e8ff',
    focusRing: '0 0 0 4px rgba(56,232,255,0.12)',
    text: '#e8f4fb',
    strong: '#e8f4fb',
    accent: '#38e8ff',
    placeholder: '#5c6b78',
    disabledText: 'rgba(232,244,251,0.75)',
    disabledPlaceholder: 'rgba(232,244,251,0.35)'
  }
} as const;

export type RafflePaymentPayload = {
  entry: EntryFormValues;
  selectedPack: PackItem;
  standaloneItems: PackItem[];
  subscriptionItems: PackItem[];
  signupCodeBonusEntries?: number;
  signupCodeBonusSource?: 'giveaway_subs' | 'signup_code';
  signupSource?: 'giveaway' | 'giveaway-subs' | 'giveaway-offer' | 'promo-pack';
  raffleUnlockToken?: string | null;

  hasActiveSubscription?: boolean;
};

type EmailStatus = 'idle' | 'checking' | 'new';


const RaffleEntryForm = ({
  packageName,
  paymentPath = 'special-offer',
  persistToSession = true,
  onReadyToPay,
  selectedPack,
  standaloneItems,
  subscriptionItems,
  signupSource = 'giveaway',
  surface = 'light',
  initialEntry
}: RaffleEntryFormProps) => {
  /* Named `skin`, not `c`: this file already binds `c` as a lambda parameter in
     the country lookups below, and a shadowed palette would be a silent trap. */
  const skin = ENTRY_SURFACES[surface];

  /* MUI renders a Select's menu in a PORTAL, outside this component's tree, so
   * the dark form cannot reach it with CSS — it has to be handed down as props.
   * Without this the country and state dropdowns open as white panels over a
   * dark dialog. Left undefined on the light surface so MUI's defaults, and the
   * existing caller's appearance, are untouched. */
  const menuProps =
    surface === 'dark'
      ? {
          PaperProps: {
            sx: {
              background: '#0f1620',
              border: `1px solid ${skin.cardBorder}`,
              '& .MuiMenuItem-root': {
                color: skin.text,
                '&:hover': { background: 'rgba(56,232,255,0.10)' },
                '&.Mui-selected': {
                  background: 'rgba(56,232,255,0.16)',
                  '&:hover': { background: 'rgba(56,232,255,0.22)' }
                }
              }
            }
          }
        }
      : undefined;

  const navigate = useNavigate();

  const { post } = useNetwork();

  const countries = useMemo(() => Country.getAllCountries(), []);
  const defaultCountryIso2 = 'AU';

  const [values, setValues] = useState<EntryFormValues>(() => {
    const defaultCountry =
      countries.find((c) => c.isoCode === defaultCountryIso2) ?? countries[0];

    const callingCode = defaultCountry?.isoCode
      ? String(getCountryCallingCode(defaultCountry.isoCode as any))
      : '';

    const defaults: EntryFormValues = {
      fullName: '',
      firstName: '',
      lastName: '',
      username: '',
      email: '',
      countryIso2: defaultCountry?.isoCode ?? '',
      countryName: defaultCountry?.name ?? '',
      stateIso2: '',
      stateName: '',
      phoneCountryCode: callingCode,
      phone: '',
      agreeToTerms: false,
      eligible: ''
    };
    if (!initialEntry) return defaults;
    // only what the caller actually knows; an undefined key keeps the default
    const given = Object.fromEntries(
      Object.entries(initialEntry).filter(([, v]) => v !== undefined && v !== '')
    ) as Partial<EntryFormValues>;
    return { ...defaults, ...given };
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const [emailAttempted, setEmailAttempted] = useState(false);
  const [fullSubmitAttempted, setFullSubmitAttempted] = useState(false);

  const [emailStatus, setEmailStatus] = useState<EmailStatus>('idle');
  const [emailChecked, setEmailChecked] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState<
    boolean | null
  >(null);

  const states = useMemo(() => {
    if (!values.countryIso2) return [];
    return State.getStatesOfCountry(values.countryIso2) || [];
  }, [values.countryIso2]);

  const setField = <K extends keyof EntryFormValues>(
    key: K,
    val: EntryFormValues[K]
  ) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const markTouched = (key: keyof EntryFormValues) =>
    setTouched((t) => ({ ...t, [key]: true }));

  const firstNameOk = (s: string) =>
    /^[\p{L}]+(?:['-][\p{L}]+)*$/u.test(s.trim());

  const lastNameOk = (s: string) =>
    /^[\p{L}]+(?:[ '-][\p{L}]+)*$/u.test(s.trim());

  // Optional extra safety
  const minLen = (s: string, n: number) => s.trim().length >= n;

  const splitFullName = (name: string) => {
    const parts = name.trim().replace(/\s+/g, ' ').split(' ').filter(Boolean);
    const firstName = parts[0] ?? '';
    const lastName = parts.slice(1).join(' ');

    return { firstName, lastName };
  };

  const pillFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 999,
      background: skin.field,
      border: `1px solid ${skin.fieldBorder}`,
      transition: 'all 0.2s ease',
      overflow: 'hidden',
      '& fieldset': { border: 'none' },
      '&:hover': {
        borderColor: skin.fieldHover
      },
      '&.Mui-focused': {
        borderColor: skin.fieldFocus,
        boxShadow: skin.focusRing
      }
    },
    '& .MuiInputBase-input': {
      padding: '15px 18px',
      fontSize: 16,
      fontWeight: 700,
      lineHeight: 1.35,
      color: skin.text,
      WebkitTextFillColor: skin.text
    },
    '& .MuiInputBase-input::placeholder': {
      color: skin.placeholder,
      WebkitTextFillColor: skin.placeholder,
      opacity: 1,
      fontWeight: 500
    },
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: skin.disabledText
    },
    '& .MuiInputBase-input.Mui-disabled::placeholder': {
      color: skin.disabledPlaceholder,
      WebkitTextFillColor: skin.disabledPlaceholder
    },
    '& .MuiSelect-icon': {
      color: skin.text
    }
  };

  const truncateSelectSx = {
    minWidth: 0,
    '& .MuiSelect-select': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minWidth: 0
    }
  } as const;

  const labelSx = {
    fontSize: 14,
    fontWeight: 800,
    mb: 0.6,
    color: skin.text
  };

  const handleCountryChange = (iso2: string) => {
    const country = countries.find((c) => c.isoCode === iso2);
    const name = country?.name ?? '';

    let callingCode = '';
    try {
      callingCode = iso2 ? String(getCountryCallingCode(iso2 as any)) : '';
    } catch {
      callingCode = '';
    }

    setValues((prev) => ({
      ...prev,
      countryIso2: iso2,
      countryName: name,
      stateIso2: '',
      stateName: '',
      phoneCountryCode: callingCode,
      phone: ''
    }));
  };

  const handleStateChange = (stateIso2: string) => {
    const st = states.find((s) => s.isoCode === stateIso2);
    setValues((prev) => ({
      ...prev,
      stateIso2,
      stateName: st?.name ?? ''
    }));
  };

  const isEmailValid = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

  const resetEmailCheck = () => {
    setEmailStatus('idle');
    setEmailChecked(false);
    setHasActiveSubscription(null);
  };

  /* This form used to ask the server whether the address was already ours, then
   * show a "welcome back" banner, hide the rest of the fields and skip straight to
   * payment. That was an account-enumeration oracle anyone could read by typing an
   * address, and it leaked whether the person subscribes as well.
   *
   * Everyone now fills the same form. For an address we already know, the server
   * keys on the email alone — ensureCustomerAndUser applies names, phone and
   * country only as findOrCreate defaults, so an existing account is never
   * overwritten and never rejected over details that no longer match. The extra
   * typing is the price of not telling strangers who our members are.
   */

  /* Validates the address and nothing else — it asks the server no questions. */
  const handleEmailCheck = async (): Promise<'new' | null> => {
    const email = values.email.trim().toLowerCase();

    setEmailAttempted(true);
    markTouched('email');

    if (!email || !isEmailValid(email)) return null;

    setEmailStatus('new');
    setEmailChecked(true);
    setHasActiveSubscription(false);
    setFullSubmitAttempted(false);
    return 'new';
  };

  const validatePhone = (
    localPhone: string,
    countryIso2: string,
    callingCode: string
  ) => {
    const local = localPhone.replace(/\s/g, '').trim();
    if (!local) return { valid: false, msg: 'Phone number is required.' };
    if (!/^\d+$/.test(local))
      return { valid: false, msg: 'Phone number can only contain numbers.' };

    const fullPhone = `+${callingCode}${local}`;

    try {
      const parsed = parsePhoneNumberFromString(fullPhone, countryIso2 as any);
      if (!parsed) return { valid: false, msg: 'Enter a valid phone number.' };
      if (!parsed.isPossible())
        return {
          valid: false,
          msg: 'Phone number looks too short or too long.'
        };
      if (!parsed.isValid())
        return {
          valid: false,
          msg: 'Enter a valid phone number for your country.'
        };
      return { valid: true, msg: '' };
    } catch {
      return { valid: false, msg: 'Enter a valid phone number.' };
    }
  };

  const phoneCheck = validatePhone(
    values.phone,
    values.countryIso2,
    values.phoneCountryCode
  );

  const parsedName = splitFullName(values.fullName);

  const errors = {
    email: values.email.trim()
      ? isEmailValid(values.email)
        ? ''
        : 'Please enter a valid email.'
      : 'Email is required.',

    fullName: values.fullName.trim()
      ? minLen(parsedName.firstName, 2) &&
        firstNameOk(parsedName.firstName) &&
        minLen(parsedName.lastName, 2) &&
        lastNameOk(parsedName.lastName)
        ? ''
        : 'Enter your first and last name.'
      : 'Full name is required.',

    state: values.stateIso2 ? '' : 'State/Province is required.',

    phone: phoneCheck.valid ? '' : phoneCheck.msg,

    agreeToTerms: values.agreeToTerms ? '' : 'You must accept the terms.',

    eligible:
      values.eligible === 'yes' || values.eligible === 'no'
        ? ''
        : 'Please choose an option.'
  };

  const eligibleValid = values.eligible === 'yes' || values.eligible === 'no';
  /* Nothing is treated as an existing account in this form any more — see the
   * note by handleEmailCheck. Kept as a constant so the shape of the code below
   * still reads as "the full form, always" rather than silently losing a branch. */
  const isExistingEmail = false;

  const canSubmitFullForm =
    !isExistingEmail &&
    emailStatus !== 'checking' &&
    !errors.email &&
    !errors.fullName &&
    !errors.state &&
    !errors.phone &&
    values.agreeToTerms &&
    eligibleValid;

  const captureCheckoutLead = async (opts?: {
    unlockToken?: string | null;
  }) => {
    if (!selectedPack) return;

    const phoneForDb =
      `+${values.phoneCountryCode} ${values.phone.trim()}`.trim();

    try {
      await post('/checkout/capture-giveaway-checkout-lead', {
        entry: {
          ...values,
          firstName: parsedName.firstName,
          lastName: parsedName.lastName,
          email: values.email.trim().toLowerCase(),
          phone: phoneForDb
        },
        selectedPack: {
          id: selectedPack.id,
          title: selectedPack.title,
          price: selectedPack.price
        },
        raffleUnlockToken: opts?.unlockToken ?? null,
        source: 'giveaway_payment_page'
      });
    } catch (error) {
      console.error('Failed to capture giveaway checkout lead:', error);
    }
  };

  const goToPayment = async (opts?: { unlockToken?: string | null }) => {
    if (!selectedPack) return;

    const phoneForDb =
      `+${values.phoneCountryCode} ${values.phone.trim()}`.trim();

    const payload: RafflePaymentPayload = {
      entry: {
        ...values,
        firstName: parsedName.firstName,
        lastName: parsedName.lastName,
        email: values.email.trim().toLowerCase(),
        phone: phoneForDb
      },
      selectedPack,
      standaloneItems,
      subscriptionItems,
      signupSource,
      raffleUnlockToken: opts?.unlockToken ?? null,
      hasActiveSubscription: Boolean(hasActiveSubscription)
    };

    if (persistToSession) {
      sessionStorage.setItem('rafflePaymentPayload', JSON.stringify(payload));
      sessionStorage.setItem('raffleEntryForm', JSON.stringify(payload.entry));
    }

    await captureCheckoutLead(opts);

    if (onReadyToPay) {
      await onReadyToPay(payload);
      return;
    }

    window.scrollTo(0, 0);
    navigate(paymentPath, { state: payload });
  };

  const handleSubmitNewUser = async (
    event?: React.MouseEvent<HTMLButtonElement>
  ) => {
    event?.preventDefault();
    event?.stopPropagation();

    setFullSubmitAttempted(true);
    if (!selectedPack) {
      trackFormError('giveaway_entry', ['pack'], { reason: 'no_pack_selected' });
      return;
    }

    const checkedStatus =
      !emailChecked && !errors.email ? await handleEmailCheck() : emailStatus;

    if (!emailChecked && checkedStatus === null) return;

    if (!canSubmitFullForm) {
      trackFormError('giveaway_entry', ['form'], { reason: 'incomplete' });
      return;
    }

    /* No phone-duplicate probe here.
     *
     * /auth/check-phone answers "is this number registered?" to anyone who asks,
     * which is the same oracle as the email check this form deliberately stopped
     * calling — and it broke the flow it was supposed to protect: a returning buyer
     * typing their OWN number was told it belonged to another account and could not
     * get past this step at all, because the message also feeds errors.phone and
     * keeps the submit button disabled.
     *
     * The server is the right place for it and already does it correctly: an account
     * matched by email skips the check entirely, and only a genuinely new address
     * whose number is already on someone else's account is refused
     * (ensureCustomerAndUser) — as the guest-safe message, so it stays uninformative
     * to strangers. */
    await goToPayment({ unlockToken: null });
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 380,
        mx: 'auto'
      }}
    >
      <Box
        sx={{
          borderRadius: 4,
          border: `1px solid ${skin.cardBorder}`,
          background: skin.card,
          boxShadow: skin.cardShadow,
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none'
          }}
        />

        <Box sx={{ p: { xs: 3, sm: 3.5 }, position: 'relative' }}>
          <Typography
            sx={{
              fontSize: { xs: 30, sm: 34 },
              fontWeight: 900,
              lineHeight: 1.05,
              letterSpacing: '-0.03em',
              color: skin.strong
            }}
          >
            Complete Your Entry
          </Typography>

          <Typography
            sx={{
              mt: 1,
              // fontSize: { xs: 14, sm: 15 },
              fontWeight: 800,
              color: skin.strong
            }}
            variant="h6"
          >
            {packageName}
          </Typography>

          <Box sx={{ mt: 4 }}>
            <Typography sx={{ ...labelSx }}>Email Address</Typography>
            <TextField
              fullWidth
              type="email"
              placeholder="john@example.com"
              value={values.email}
              onChange={(e) => {
                setField('email', e.target.value);
                resetEmailCheck();
              }}
              onBlur={() => {
                markTouched('email');
                handleEmailCheck();
              }}
              error={(touched.email || emailAttempted) && !!errors.email}
              helperText={
                emailStatus === 'checking'
                  ? 'Checking email...'
                  : touched.email || emailAttempted
                    ? errors.email
                    : ''
              }
              FormHelperTextProps={{
                sx: {
                  color: skin.strong,
                  fontWeight: 700,
                  ml: 0
                }
              }}
              inputProps={{
                autoComplete: 'email',
                inputMode: 'email'
              }}
              sx={pillFieldSx}
            />


            {!isExistingEmail && (
              <>
                <Box sx={{ mt: 3 }}>
                  <Typography sx={labelSx}>Full Name</Typography>
                  <TextField
                    fullWidth
                    placeholder="John O'Connor"
                    value={values.fullName}
                    onChange={(e) => setField('fullName', e.target.value)}
                    onBlur={() => markTouched('fullName')}
                    error={
                      (touched.fullName || fullSubmitAttempted) &&
                      !!errors.fullName
                    }
                    helperText={
                      touched.fullName || fullSubmitAttempted
                        ? errors.fullName || ' '
                        : ' '
                    }
                    FormHelperTextProps={{
                      sx: {
                        color: skin.strong,
                        fontWeight: 700,
                        ml: 0
                      }
                    }}
                    sx={pillFieldSx}
                  />
                </Box>

                {/* Country/State */}
                <Box sx={{ mt: 1 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2.2
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={labelSx}>Country</Typography>
                      <FormControl
                        fullWidth
                        sx={{ ...pillFieldSx, ...truncateSelectSx }}
                      >
                        <Select
                          value={values.countryIso2}
                          onChange={(e) =>
                            handleCountryChange(String(e.target.value))
                          }
                          displayEmpty
                          MenuProps={menuProps}
                          sx={{ color: skin.text, fontWeight: 700 }}
                        >
                          {countries.map((c) => (
                            <MenuItem key={c.isoCode} value={c.isoCode}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={labelSx}>State/Province</Typography>

                      <FormControl
                        fullWidth
                        error={
                          (touched.stateIso2 || fullSubmitAttempted) &&
                          !!errors.state
                        }
                        sx={{ ...pillFieldSx, ...truncateSelectSx }}
                      >
                        <Select
                          value={values.stateIso2}
                          onChange={(e) =>
                            handleStateChange(String(e.target.value))
                          }
                          onBlur={() => markTouched('stateIso2')}
                          disabled={states.length === 0}
                          displayEmpty
                          MenuProps={menuProps}
                          sx={{ color: skin.text, fontWeight: 700 }}
                        >
                          {states.length === 0 ? (
                            <MenuItem value="" disabled>
                              No states available
                            </MenuItem>
                          ) : (
                            states.map((s) => (
                              <MenuItem key={s.isoCode} value={s.isoCode}>
                                {s.name}
                              </MenuItem>
                            ))
                          )}
                        </Select>

                        {/* ✅ helper text */}
                        <Typography
                          component="div"
                          sx={{
                            minHeight: 20,
                            mt: 0.6,
                            fontWeight: 800,
                            color:
                              (touched.stateIso2 || fullSubmitAttempted) &&
                              errors.state
                                ? 'rgba(255,120,120,0.9)'
                                : 'transparent'
                          }}
                        >
                          {(touched.stateIso2 || fullSubmitAttempted) &&
                          errors.state
                            ? errors.state
                            : ' '}
                        </Typography>
                      </FormControl>
                    </Box>
                  </Box>
                </Box>

                {/* Phone */}
                <Box sx={{ mt: 3 }}>
                  <Typography sx={labelSx}>Phone Number</Typography>
                  <TextField
                    fullWidth
                    placeholder="412 345 678"
                    value={values.phone}
                    onChange={(e) =>
                      setField('phone', e.target.value.replace(/[^\d\s]/g, ''))
                    }
                    onBlur={() => markTouched('phone')}
                    error={
                      (touched.phone || fullSubmitAttempted) && !!errors.phone
                    }
                    helperText={
                      touched.phone || fullSubmitAttempted
                        ? errors.phone || ' '
                        : ' '
                    }
                    FormHelperTextProps={{
                      sx: {
                        color: skin.strong,
                        fontWeight: 700,
                        ml: 0
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: skin.strong,
                              mr: 0.5
                            }}
                          >
                            +{values.phoneCountryCode}
                          </Typography>
                        </InputAdornment>
                      ),
                      inputMode: 'tel',
                      autoComplete: 'tel-national'
                    }}
                    sx={pillFieldSx}
                  />
                </Box>

                {/* Terms */}
                <Box sx={{ mt: 3 }}>
                  <FormControlLabel
                    sx={{
                      alignItems: 'flex-start',
                      m: 0,
                      '& .MuiFormControlLabel-label': {
                        fontSize: 14,
                        fontWeight: 700,
                        color: skin.strong,
                        lineHeight: 1.25
                      }
                    }}
                    control={
                      <Checkbox
                        checked={values.agreeToTerms}
                        onChange={(e) =>
                          setField('agreeToTerms', e.target.checked)
                        }
                        sx={{
                          mt: 0.2,
                          color: skin.strong,
                          '&.Mui-checked': { color: skin.accent }
                        }}
                      />
                    }
                    label={
                      <Box component="span">
                        I have read and agree to the{' '}
                        <Box
                          component={RouterLink}
                          to="https://incrypt-public-assets-global.s3.ap-southeast-2.amazonaws.com/documents/terms_and_conditions_05_08_2026.docx"
                          sx={{
                            color: skin.accent,
                            textDecoration: 'underline',
                            textUnderlineOffset: '3px',
                            '&:hover': { opacity: 0.85 }
                          }}
                        >
                          terms and conditions
                        </Box>
                      </Box>
                    }
                  />
                </Box>

                {fullSubmitAttempted && !values.agreeToTerms && (
                  <Typography
                    sx={{
                      mt: 1,
                      fontWeight: 800,
                      color: 'rgba(255,120,120,0.9)'
                    }}
                  >
                    {errors.agreeToTerms}
                  </Typography>
                )}

                {/* Eligible */}
                <Box sx={{ mt: 3 }}>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 750,
                      color: skin.strong
                    }}
                  >
                    You have read the T&amp;Cs and are eligible for giveaways{' '}
                    <Box component="span" sx={{ color: '#ff5a5a' }}>
                      *
                    </Box>
                  </Typography>

                  <RadioGroup
                    value={values.eligible}
                    onChange={(e) =>
                      setField(
                        'eligible',
                        e.target.value as EntryFormValues['eligible']
                      )
                    }
                    onBlur={() => markTouched('eligible')}
                    sx={{ mt: 2 }}
                  >
                    <FormControlLabel
                      value="yes"
                      control={
                        <Radio
                          sx={{
                            color: skin.strong,
                            '&.Mui-checked': { color: skin.accent }
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: skin.strong
                          }}
                        >
                          Yes I am eligible for giveaways
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      value="no"
                      control={
                        <Radio
                          sx={{
                            color: skin.strong,
                            '&.Mui-checked': { color: skin.accent }
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: skin.strong
                          }}
                        >
                          No I am not eligible for giveaways
                        </Typography>
                      }
                    />
                  </RadioGroup>

                  {!!touched.eligible && !eligibleValid && (
                    <Typography
                      sx={{
                        mt: 1,
                        fontWeight: 800,
                        color: 'rgba(255,120,120,0.9)'
                      }}
                    >
                      Please choose an option.
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    mt: 1.25,
                    p: 1.4,
                    borderRadius: 2,
                    border: `1px solid ${skin.strong}`,
                    background:
                      values.eligible === 'no'
                        ? 'rgba(255, 90, 90, 0.08)'
                        : 'rgba(255,255,255,0.05)'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 800,
                      color:
                        values.eligible === 'no'
                          ? 'rgba(255,140,140,0.95)'
                          : skin.strong,
                      lineHeight: 1.35
                    }}
                  >
                    <Box component="span" sx={{ color: '#ff5a5a', mr: 0.6 }}>
                      *
                    </Box>
                    If you select <b>No</b>, you can still receive Digital Collectibles, but you
                    will <b>not</b> receive giveaway entries and will <b>not</b>{' '}
                    be included in the giveaway draw.
                  </Typography>
                </Box>
              </>
            )}

            <Box sx={{ mt: 3 }}>
              <Button
                fullWidth
                type="button"
                disabled={
                  emailStatus === 'checking' ||
                  !canSubmitFullForm
                }
                onMouseDown={(event) => event.stopPropagation()}
                onClick={handleSubmitNewUser}
                sx={{
                  height: 48,
                  fontSize: 15,
                  fontWeight: 900,
                  borderRadius: 999,
                  color: 'white',
                  backgroundColor: 'green',
                  '&:hover': {
                    background: 'darkgreen'
                  }
                }}
              >
                TAP TO CONTINUE
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RaffleEntryForm;
